/*
 * BuyerDropshipSupplierDiscovery — the missing discovery path.
 *
 * Before this component existed, a buyer's only way to set up dropship
 * with a seller was to know the seller's UUID and navigate to
 * /dropship/terms/{seller_id} directly. There was no UI surface that
 * listed which connected sellers offered dropship.
 *
 * This component fills that gap:
 *   - Fetches `/dropship/eligible-suppliers` (connected sellers with
 *     `policies.dropship_eligible = true`).
 *   - Renders each as a card with the seller's logo + name + terms
 *     status badge.
 *   - "Set up terms" CTA navigates to the existing TermsEditor flow.
 *
 * Mounts on the `Store Setup` tab of /buyer/dropship.
 */
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../lib/api";
import { Button } from "./ui/button";
import { Sparkles, CheckCircle2, ChevronRight, Loader2, Store } from "lucide-react";

const TERMS_BADGE = {
  none:              { label: "Not set up",       cls: "bg-rose-100 text-rose-800 border-rose-200" },
  draft:             { label: "Draft saved",       cls: "bg-amber-100 text-amber-800 border-amber-200" },
  pending_signature: { label: "Awaiting signature", cls: "bg-amber-100 text-amber-800 border-amber-200" },
  active:            { label: "Active",             cls: "bg-emerald-100 text-emerald-800 border-emerald-200" },
};

export default function BuyerDropshipSupplierDiscovery() {
  const nav = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/dropship/eligible-suppliers");
        if (!cancelled) setItems(data?.items || []);
      } catch {
        if (!cancelled) setItems([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  if (loading) {
    return (
      <div className="rounded-2xl border border-foreground/10 bg-white/60 px-5 py-4 text-[12.5px] text-foreground/50" data-testid="buyer-dropship-discovery-loading">
        <Loader2 className="w-3.5 h-3.5 inline-block animate-spin mr-2"/>
        Checking which connected suppliers offer dropship…
      </div>
    );
  }

  if (items.length === 0) {
    return (
      <div
        className="rounded-2xl border border-dashed border-foreground/15 bg-foreground/[0.02] px-5 py-5"
        data-testid="buyer-dropship-discovery-empty"
      >
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-xl grid place-items-center bg-foreground/[0.08] shrink-0">
            <Store className="w-5 h-5 text-foreground/45"/>
          </div>
          <div>
            <div className="text-[13px] font-medium">No connected suppliers offer dropship yet</div>
            <div className="text-[11.5px] text-foreground/65 mt-0.5 leading-snug">
              When one of your connected suppliers enables dropship in their
              settings, they&apos;ll appear here so you can sign terms and start
              auto-forwarding orders.
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="buyer-dropship-discovery">
      <div className="flex items-center justify-between mb-3">
        <div className="text-xs mono uppercase tracking-[0.18em] text-foreground/55">
          Suppliers offering dropship ({items.length})
        </div>
        <div className="text-[11px] text-foreground/45">
          Connected to your account · waiting for you to set up terms
        </div>
      </div>
      <div className="grid sm:grid-cols-2 gap-3">
        {items.map(s => {
          const badge = TERMS_BADGE[s.terms_status] || TERMS_BADGE.none;
          const isActive = s.terms_status === "active";
          const heroBg = s.branding?.primary_color || "#0a1f1c";
          return (
            <div
              key={s.tenant_id}
              className="rounded-2xl border border-foreground/10 bg-white p-4 flex flex-col gap-3 hover:border-foreground/20 transition"
              data-testid={`buyer-dropship-supplier-${s.tenant_id}`}
            >
              <div className="flex items-center gap-3">
                <div
                  className="w-11 h-11 rounded-lg grid place-items-center overflow-hidden shrink-0"
                  style={{ background: heroBg, color: s.branding?.accent_color || "#fff" }}
                >
                  {s.branding?.logo_square_url ? (
                    <img src={s.branding.logo_square_url} alt={s.tenant_name} className="w-full h-full object-cover"/>
                  ) : (
                    <span className="text-base font-bold display">{(s.tenant_name || "?").charAt(0).toUpperCase()}</span>
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-[13.5px] font-semibold truncate">{s.tenant_name || "Unknown"}</div>
                  <div className="text-[10.5px] text-foreground/55 mt-0.5">
                    Ships within {s.shipping_sla_hours}h
                  </div>
                </div>
              </div>

              <div className="flex items-center justify-between">
                <span
                  className={`inline-block text-[10px] font-semibold tracking-wider uppercase px-2 py-0.5 rounded border ${badge.cls}`}
                  data-testid={`buyer-dropship-status-${s.tenant_id}`}
                >
                  {badge.label}
                </span>

                {isActive ? (
                  <span className="text-[11.5px] text-emerald-700 inline-flex items-center gap-1">
                    <CheckCircle2 className="w-3.5 h-3.5"/>
                    Ready to receive orders
                  </span>
                ) : (
                  <Button
                    size="sm"
                    onClick={() => nav(`/dropship/terms/${s.seller_id}`)}
                    data-testid={`buyer-dropship-setup-${s.tenant_id}`}
                    className="rounded-full bg-foreground text-background hover:bg-foreground/90 text-[12px] h-8 px-3 gap-1"
                  >
                    <Sparkles className="w-3 h-3"/>
                    Set up terms
                    <ChevronRight className="w-3 h-3"/>
                  </Button>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
